import { Box, Checkbox, CircularProgress, FormControlLabel, TextField, Typography } from '@mui/material';
import Image1 from '../../images/courses/image1.png';
import { AsyncPaginate } from 'react-select-async-paginate';
import { useContext, useEffect, useState } from 'react';
import Fetch from '../../services/Fetch';
import MultipleSelectChip from '../../components/MultiSelect';
import AuthContext from '../../context/AuthContext';
import SnackbarAlert from '../../components/SnackBar';
import { useNavigate } from 'react-router-dom';
import useSnackBar from '../../hooks/UseSnackBar';
import { useWaits } from '../../hooks/UseWait';
import { buildCourseFormData } from '../../helper/CourseFormData';
import { useConstants } from '../../hooks/UseConstants';
import { parsePaginatedResponse } from '../../utils/parsePaginatedResponse';
import FormSideImageLayout, { previewImageFile } from '../../components/FormSideImageLayout';
import PageContent from '../../components/PageContent';
import PageHero from '../../components/motion/PageHero';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

function CreateCourse() {
     const { host, language } = useConstants();
     const { wait } = useContext(AuthContext);
     const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
     const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
     const [majors, setMajors] = useState([]);
     const [categories, setCategories] = useState([]);
     const [selectedMajors, setSelectedMajors] = useState([]);
     const [option, setOption] = useState('');
     const [image, setImage] = useState('');
     const [nameEn, setNameEn] = useState('');
     const [nameAr, setNameAr] = useState('');
     const [descriptionEn, setDescriptionEn] = useState('');
     const [descriptionAr, setDescriptionAr] = useState('');
     const [categoryId, setCategoryId] = useState('');
     const [price, setPrice] = useState('');
     const [discountPrice, setDiscountPrice] = useState('');
     const [discountStartAt, setDiscountStartAt] = useState('');
     const [discountEndAt, setDiscountEndAt] = useState('');
     const [isFree, setIsFree] = useState(false);
     const [levelAr, setLevelAr] = useState('');
     const [levelEn, setLevelEn] = useState('');
     const [objectivesAr, setObjectivesAr] = useState('');
     const [objectivesEn, setObjectivesEn] = useState('');
     const [prerequisitesAr, setPrerequisitesAr] = useState('');
     const [prerequisitesEn, setPrerequisitesEn] = useState('');
     const [introVideo, setIntroVideo] = useState('');
     const navigate = useNavigate();

     const loadOptions = async (search, loadedOptions, { page }) => {
          const result = await Fetch(
               `${host}/categories?search=${encodeURIComponent(search || '')}&page=${page}&per_page=20`,
               'GET',
               null
          );
          const { items, pagination } = parsePaginatedResponse(result);
          setCategories(items);
          return {
               options: items.map(
                    item => ({ value: item.id, label: language === 'ar' ? item.name_ar : item.name_en })
               ),
               hasMore: pagination.current_page < pagination.last_page,
               additional: { page: page + 1 },
          };
     };

     const getCategories = async () => {
          const result = await Fetch(`${host}/categories?per_page=100`, 'GET', null);
          const { items } = parsePaginatedResponse(result);
          setCategories(items);
     };

     const getMajors = async () => {
          const result = await Fetch(`${host}/majors?per_page=100`, 'GET', null);
          const { items } = parsePaginatedResponse(result);
          setMajors(items);
     };

     const createCourse = async () => {
          setSendWait(true);
          const formData = buildCourseFormData({
               nameEn, nameAr, descriptionEn, descriptionAr, price, discountPrice,
               discountStartAt, discountEndAt,
               isFree, levelAr, levelEn, objectivesAr, objectivesEn, prerequisitesAr, prerequisitesEn,
               introVideo, image, categoryId, majors: selectedMajors,
          });
          let result = await Fetch(host + '/teacher/courses/store', 'POST', formData);
          if (result.status === 200) {
               navigate('/my-courses');
          } else if (result.status === 422) {
               setSnackBar('error', result.data.errors[0]);
          }
          setSendWait(false);
     };

     useEffect(() => {
          getCategories();
          getMajors();
          setGetWait(false);
     }, []);

     const fieldSx = { width: '100%' };

     return (
          <>
               <PageContent wait={wait} loading={getWait}>
                    <PageHero
                        title={language === 'ar' ? 'إنشاء دورة' : 'Create course'}
                        subtitle={language === 'ar' ? 'أضف دورة جديدة لطلابك' : 'Add a new course for your students'}
                        icon={<AddCircleOutlineIcon />}
                    />
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 3, borderRadius: 3, overflow: 'hidden' }}>
                         <FormSideImageLayout
                              title={language === 'ar' ? 'إضافة دورة جديدة' : 'Add New Course'}
                              illustrationSrc={Image1}
                              previewId="course-create-image"
                              uploadLabel={language === 'ar' ? 'صورة الدورة' : 'Course cover'}
                              onImageChange={(e) => {
                                   previewImageFile(e, 'course-create-image');
                                   setImage(e.target.files[0]);
                              }}
                              onSubmit={createCourse}
                              submitLabel={language === 'ar' ? 'إنشاء الدورة' : 'Create'}
                              loading={sendWait}
                         >
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                   <TextField sx={fieldSx} onChange={(e) => setNameEn(e.target.value)} label={language === 'ar' ? 'الاسم بالإنجليزية' : 'English Name'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} onChange={(e) => setNameAr(e.target.value)} label={language === 'ar' ? 'الاسم بالعربية' : 'Arabic Name'} variant="outlined" size="small" />
                              </Box>
                              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                   <TextField sx={fieldSx} onChange={(e) => setDescriptionEn(e.target.value)} label={language === 'ar' ? 'الوصف بالإنجليزية' : 'English Description'} multiline rows={3} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} onChange={(e) => setDescriptionAr(e.target.value)} label={language === 'ar' ? 'الوصف بالعربية' : 'Arabic Description'} multiline rows={3} variant="outlined" size="small" />
                              </Box>
                              <Box sx={{ mt: 2 }}>
                                   <AsyncPaginate
                                        className="text-black"
                                        value={option}
                                        loadOptions={loadOptions}
                                        onChange={(opt) => { setOption(opt); setCategoryId(opt?.value); }}
                                        placeholder={language === 'ar' ? 'اختر التصنيف' : 'Select Category'}
                                        additional={{ page: 1 }}
                                   />
                              </Box>
                              <Box sx={{ mt: 2 }}>
                                   <MultipleSelectChip title={language === 'ar' ? 'التخصصات' : 'Majors'} data={majors} onChange={setSelectedMajors} />
                              </Box>
                              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'center' }}>
                                   <TextField sx={fieldSx} type="number" onChange={(e) => setPrice(e.target.value)} label={language === 'ar' ? 'السعر' : 'Price'} variant="outlined" size="small" disabled={isFree} />
                                   <TextField sx={fieldSx} type="number" onChange={(e) => setDiscountPrice(e.target.value)} label={language === 'ar' ? 'سعر الخصم' : 'Discount Price'} variant="outlined" size="small" disabled={isFree} />
                              </Box>
                              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                   <TextField
                                        sx={fieldSx}
                                        type="date"
                                        onChange={(e) => setDiscountStartAt(e.target.value)}
                                        label={language === 'ar' ? 'بداية الخصم' : 'Discount starts'}
                                        variant="outlined"
                                        size="small"
                                        disabled={isFree}
                                        InputLabelProps={{ shrink: true }}
                                   />
                                   <TextField
                                        sx={fieldSx}
                                        type="date"
                                        onChange={(e) => setDiscountEndAt(e.target.value)}
                                        label={language === 'ar' ? 'نهاية الخصم' : 'Discount ends'}
                                        variant="outlined"
                                        size="small"
                                        disabled={isFree}
                                        InputLabelProps={{ shrink: true }}
                                   />
                              </Box>
                              <FormControlLabel
                                   sx={{ mt: 1 }}
                                   control={<Checkbox checked={isFree} onChange={(e) => setIsFree(e.target.checked)} />}
                                   label={language === 'ar' ? 'دورة مجانية' : 'Free course'}
                              />
                              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                   <TextField sx={fieldSx} onChange={(e) => setLevelEn(e.target.value)} label={language === 'ar' ? 'المستوى (إنجليزي)' : 'Level (EN)'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} onChange={(e) => setLevelAr(e.target.value)} label={language === 'ar' ? 'المستوى (عربي)' : 'Level (AR)'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} multiline rows={2} onChange={(e) => setObjectivesEn(e.target.value)} label={language === 'ar' ? 'الأهداف بالإنجليزية (سطر لكل هدف)' : 'Objectives (EN)'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} multiline rows={2} onChange={(e) => setObjectivesAr(e.target.value)} label={language === 'ar' ? 'الأهداف (سطر لكل هدف)' : 'Objectives (AR)'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} multiline rows={2} onChange={(e) => setPrerequisitesEn(e.target.value)} label={language === 'ar' ? 'المتطلبات بالإنجليزية' : 'Prerequisites (EN)'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} multiline rows={2} onChange={(e) => setPrerequisitesAr(e.target.value)} label={language === 'ar' ? 'المتطلبات بالعربية' : 'Prerequisites (AR)'} variant="outlined" size="small" />
                              </Box>
                              <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                                   <Typography variant="body2" color="text.secondary" className="!mb-1">
                                        {language === 'ar' ? 'فيديو تعريفي' : 'Intro video'}
                                   </Typography>
                                   <input type="file" accept="video/*" onChange={(e) => setIntroVideo(e.target.files[0])} />
                              </Box>
                         </FormSideImageLayout>
                    </Box>
               </PageContent>
               <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
          </>
     );
}

export default CreateCourse;
